/**
 * Position fixed overlays (date picker, dropdown panels, row Actions menus)
 * relative to a trigger, flipping above when there is not enough viewport space below.
 */

export interface FloatingPanelPosition {
  top: number;
  left: number;
  placement: 'above' | 'below';
}

export interface DropdownPanelLayout {
  left: number;
  minWidth: number;
  maxOptionsHeight: number;
  placement: 'above' | 'below';
  /** Set when placement is below */
  top?: number;
  /** Set when placement is above — panel grows upward from the trigger */
  bottom?: number;
}

/** Right-aligned overflow / Actions menu (Users, Semesters, roster, etc.) */
export interface ActionsMenuPosition {
  top: number;
  right: number;
  placement: 'above' | 'below';
  /**
   * When the preferred side cannot fit the full menu, constrain height and scroll.
   * Omit when the full estimated height fits on the chosen side.
   */
  maxHeight?: number;
}

export interface FloatingPanelPositionOptions {
  gap?: number;
  viewportPadding?: number;
}

const DEFAULT_PANEL_HEIGHT = 340;
const DEFAULT_PANEL_WIDTH = 280;
const DEFAULT_DROPDOWN_MAX_OPTIONS_HEIGHT = 280;
/** Typical row Actions menu (~4–5 items); callers can override. */
const DEFAULT_ACTIONS_MENU_HEIGHT = 200;

export function computeFloatingPanelPosition(
  triggerRect: DOMRect,
  panelHeight: number = DEFAULT_PANEL_HEIGHT,
  panelWidth: number = DEFAULT_PANEL_WIDTH,
  options?: FloatingPanelPositionOptions
): FloatingPanelPosition {
  const gap = options?.gap ?? 8;
  const padding = options?.viewportPadding ?? 8;

  const spaceBelow = window.innerHeight - triggerRect.bottom - padding;
  const spaceAbove = triggerRect.top - padding;
  const openBelow = spaceBelow >= panelHeight + gap || spaceBelow >= spaceAbove;

  const top = openBelow
    ? triggerRect.bottom + gap
    : Math.max(padding, triggerRect.top - panelHeight - gap);

  const maxLeft = window.innerWidth - panelWidth - padding;
  const left = Math.max(padding, Math.min(triggerRect.left, maxLeft));

  return {
    top,
    left,
    placement: openBelow ? 'below' : 'above',
  };
}

/**
 * Position a portaled row Actions (⋯) menu. Prefer below; flip above when clipped.
 * If neither side fits fully, pick the side with more space and set maxHeight for scroll.
 * Use with `transform: translateY(-100%)` when placement is `above` (top is the trigger’s top edge).
 */
export function computeActionsMenuPosition(
  triggerRect: DOMRect,
  estimatedMenuHeight: number = DEFAULT_ACTIONS_MENU_HEIGHT,
  options?: FloatingPanelPositionOptions
): ActionsMenuPosition {
  const gap = options?.gap ?? 4;
  const padding = options?.viewportPadding ?? 8;

  const right = Math.max(padding, window.innerWidth - triggerRect.right);
  const spaceBelow = window.innerHeight - triggerRect.bottom - padding;
  const spaceAbove = triggerRect.top - padding;
  const needed = estimatedMenuHeight + gap;

  const fitsBelow = spaceBelow >= needed;
  const fitsAbove = spaceAbove >= needed;
  const openBelow = fitsBelow || (!fitsAbove && spaceBelow >= spaceAbove);

  const available = openBelow ? spaceBelow : spaceAbove;
  const maxHeight =
    available < needed ? Math.max(0, available - gap) : undefined;

  return {
    top: openBelow ? triggerRect.bottom + gap : triggerRect.top - gap,
    right,
    placement: openBelow ? 'below' : 'above',
    ...(maxHeight !== undefined ? { maxHeight } : {}),
  };
}

/**
 * Position a Dropdown listbox in the viewport with scroll when options overflow.
 * Opens above the trigger on mobile when there is more room above than below.
 */
export function computeDropdownPanelLayout(
  triggerRect: DOMRect,
  options: {
    preferredMaxOptionsHeight?: number;
    gap?: number;
    viewportPadding?: number;
    /** When true, always open above the trigger (e.g. church field on mobile). */
    forceAbove?: boolean;
  } = {}
): DropdownPanelLayout {
  const gap = options.gap ?? 4;
  const padding = options.viewportPadding ?? 8;
  const preferredMax = options.preferredMaxOptionsHeight ?? DEFAULT_DROPDOWN_MAX_OPTIONS_HEIGHT;
  const panelChrome = 12;

  const spaceBelow = window.innerHeight - triggerRect.bottom - padding;
  const spaceAbove = triggerRect.top - padding;
  const minVisible = 120;
  const openBelow = options.forceAbove
    ? false
    : spaceBelow >= minVisible + gap || spaceBelow >= spaceAbove;

  const maxLeft = window.innerWidth - triggerRect.width - padding;
  const left = Math.max(padding, Math.min(triggerRect.left, maxLeft));

  if (openBelow) {
    const maxOptionsHeight = Math.max(
      minVisible,
      Math.min(preferredMax, spaceBelow - gap - panelChrome)
    );
    return {
      top: triggerRect.bottom + gap,
      left,
      minWidth: triggerRect.width,
      maxOptionsHeight,
      placement: 'below',
    };
  }

  const maxOptionsHeight = Math.max(
    minVisible,
    Math.min(preferredMax, spaceAbove - gap - panelChrome)
  );
  return {
    bottom: window.innerHeight - triggerRect.top + gap,
    left,
    minWidth: triggerRect.width,
    maxOptionsHeight,
    placement: 'above',
  };
}

export {
  DEFAULT_PANEL_HEIGHT,
  DEFAULT_PANEL_WIDTH,
  DEFAULT_DROPDOWN_MAX_OPTIONS_HEIGHT,
  DEFAULT_ACTIONS_MENU_HEIGHT,
};
